import { useState } from "react";

import { Box, Grid, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { MonthPicker, TextInput } from "components";
import { getRequiredFieldStyles } from "pages/clients/view/components/required-field-styles";
import { CreditRequestForm } from "types";
import { AddressUtils } from "utils";
import { useCreditRequestMode } from "../request-mode-context";

import SectionTitle from "../../../components/section-title";
import { SectionTitleIndicatorType } from "../../../components/section-title";

interface CreditRequestPersonalAddressProps {
  form: UseFormReturnType<CreditRequestForm>;
  visibleFields: string[];
  readOnly?: boolean;
  isBankCredit?: boolean;
  hasWarning?: boolean;
  hasPreviousWarning?: boolean;
  onWarningClick?: () => void;
  onPreviousWarningClick?: () => void;
  indicatorType?: SectionTitleIndicatorType;
}

const CreditRequestPersonalAddress = ({
  form,
  visibleFields,
  readOnly = false,
  isBankCredit = false,
  hasWarning = false,
  hasPreviousWarning = false,
  onWarningClick,
  onPreviousWarningClick,
  indicatorType = "warning",
}: CreditRequestPersonalAddressProps) => {
  const { t } = useTranslation();
  const { isPreliminary } = useCreditRequestMode();
  const [editingField, setEditingField] = useState<string | null>(null);

  const getRequiredMonthPickerStyles = (fieldName: "livingFrom") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = !value;

    return getRequiredFieldStyles({
      readOnly,
      isVisible: visibleFields.includes(fieldName),
      isEmpty,
    });
  };

  const getFieldKey = (fieldName: string) => {
    // Use stable key for the field being edited, form.key for others
    return editingField === fieldName ? fieldName : form.key(fieldName);
  };

  const handlePostalChange = (
    value: string,
    postalField: "postal" | "previousPostal",
    cityField: "city" | "previousCity",
  ) => {
    form.setFieldValue(postalField, value);

    const city = AddressUtils.getCityFromPostal(value);
    if (city) {
      form.setFieldValue(cityField, city, { forceUpdate: true });
    }
  };

  const handleCityChange = (
    value: string,
    cityField: "city" | "previousCity",
    postalField: "postal" | "previousPostal",
  ) => {
    form.setFieldValue(cityField, value);

    const postal = AddressUtils.getPostalFromCity(value);
    if (postal) {
      form.setFieldValue(postalField, postal, { forceUpdate: true });
    }
  };

  return (
    <Box>
      <SectionTitle
        label={t("user.address.label")}
        isBankCredit={isBankCredit}
        hasWarning={hasWarning}
        onWarningClick={onWarningClick}
        indicatorType={indicatorType}
      />
      <SimpleGrid cols={4}>
        <Grid>
          <Grid.Col span={5}>
            <TextInput
              label={t("user.address.postal.label")}
              key={getFieldKey("postal")}
              {...form.getInputProps("postal")}
              maxLength={4}
              required={!isPreliminary}
              onFocus={() => setEditingField("postal")}
              onChange={(event) => handlePostalChange(event.currentTarget.value, "postal", "city")}
              disabled={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput
              label={t("user.address.city.label")}
              key={getFieldKey("city")}
              {...form.getInputProps("city")}
              required={!isPreliminary}
              onFocus={() => setEditingField("city")}
              onChange={(event) => handleCityChange(event.currentTarget.value, "city", "postal")}
              disabled={readOnly}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={7}>
            <TextInput
              label={t("user.address.street.label")}
              key={form.key("street")}
              {...form.getInputProps("street")}
              required={!isPreliminary}
              disabled={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <TextInput
              label={t("user.address.houseNumber.label")}
              key={form.key("houseNumber")}
              {...form.getInputProps("houseNumber")}
              onBeforeInput={(event: any) => {
                const cursorPos = event.currentTarget.selectionStart || 0;
                if (cursorPos === 0) {
                  const inputValue = (event as any).data;
                  if (inputValue && !/^[0-9]/.test(inputValue)) {
                    event.preventDefault();
                  }
                }
              }}
              required={!isPreliminary}
              disabled={readOnly}
            />
          </Grid.Col>
        </Grid>
        <MonthPicker
          label={t("user.livingFrom.label")}
          key={form.key("livingFrom")}
          {...form.getInputProps("livingFrom")}
          maxDate={new Date()}
          required={!isPreliminary}
          styles={isPreliminary ? undefined : getRequiredMonthPickerStyles("livingFrom")}
          disabled={readOnly}
        />
      </SimpleGrid>
      {(visibleFields.includes("previousPostal") ||
        visibleFields.includes("previousCity") ||
        visibleFields.includes("previousStreet") ||
        visibleFields.includes("previousHouseNumber")) && (
        <Box>
          <SectionTitle
            label={t("user.address.previousLabel")}
            isBankCredit={isBankCredit}
            hasWarning={hasPreviousWarning}
            onWarningClick={onPreviousWarningClick}
            indicatorType={indicatorType}
          />
          <SimpleGrid cols={4} mt={"sm"}>
            <Grid>
              {visibleFields.includes("previousPostal") && (
                <Grid.Col span={5}>
                  <TextInput
                    label={t("user.address.postal.label")}
                    key={getFieldKey("previousPostal")}
                    {...form.getInputProps("previousPostal")}
                    required={!isPreliminary}
                    onFocus={() => setEditingField("previousPostal")}
                    onChange={(event) =>
                      handlePostalChange(
                        event.currentTarget.value,
                        "previousPostal",
                        "previousCity",
                      )
                    }
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
              {visibleFields.includes("previousCity") && (
                <Grid.Col span={7}>
                  <TextInput
                    label={t("user.address.city.label")}
                    key={getFieldKey("previousCity")}
                    {...form.getInputProps("previousCity")}
                    required={!isPreliminary}
                    onFocus={() => setEditingField("previousCity")}
                    onChange={(event) =>
                      handleCityChange(event.currentTarget.value, "previousCity", "previousPostal")
                    }
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
            </Grid>
            <Grid>
              {visibleFields.includes("previousStreet") && (
                <Grid.Col span={7}>
                  <TextInput
                    label={t("user.address.street.label")}
                    key={form.key("previousStreet")}
                    {...form.getInputProps("previousStreet")}
                    required={!isPreliminary}
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
              {visibleFields.includes("previousHouseNumber") && (
                <Grid.Col span={5}>
                  <TextInput
                    label={t("user.address.houseNumber.label")}
                    key={form.key("previousHouseNumber")}
                    {...form.getInputProps("previousHouseNumber")}
                    onBeforeInput={(event: any) => {
                      const cursorPos = event.currentTarget.selectionStart || 0;
                      if (cursorPos === 0) {
                        const inputValue = (event as any).data;
                        if (inputValue && !/^[0-9]/.test(inputValue)) {
                          event.preventDefault();
                        }
                      }
                    }}
                    required={!isPreliminary}
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
            </Grid>
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
};

export default CreditRequestPersonalAddress;
